import { Span } from "@vp/swan";
import React from "react";
import { useLocalization } from "../../hooks/useLocalizations";
import { ComparativeDisplayType } from "../../types/pricing";

interface ComparativePriceProps {
  listPrice: number;
  discountPrice: number;
  currency: string;
  locale: string;
  minimumFractionDigits: number;
  comparativeDisplayType?: ComparativeDisplayType;
}

function formatPrice(locale: string, currency: string, price: number, minimumFractionDigits: number): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
  }).format(price);
}

function shouldShowComparison(
  listPrice: number,
  discountPrice: number,
  comparativeDisplayType: ComparativeDisplayType,
): boolean {
  if (comparativeDisplayType === ComparativeDisplayType.Always) return true;
  if (comparativeDisplayType === ComparativeDisplayType.Never) return false;
  // CustomerFriendly and Legacy: show when genuinely discounted
  return listPrice !== discountPrice && listPrice > 0 && discountPrice >= 0 && discountPrice < listPrice;
}

const ComparativePrice: React.FC<ComparativePriceProps> = ({
  listPrice,
  discountPrice,
  currency,
  locale,
  minimumFractionDigits,
  comparativeDisplayType = ComparativeDisplayType.CustomerFriendly,
}) => {
  const { t } = useLocalization();
  const formattedList = formatPrice(locale, currency, listPrice, minimumFractionDigits);
  const formattedDiscount = formatPrice(locale, currency, discountPrice, minimumFractionDigits);
  const showComparison = shouldShowComparison(listPrice, discountPrice, comparativeDisplayType);

  if (showComparison) {
    return (
      <Span className="swan-pricing-comparison">
        <Span className="swan-list-price">
          <Span className="swan-visually-hidden">{t("price-platform.token-price.original-price")}</Span>
          {formattedList}
        </Span>{" "}
        <Span className="swan-discount-price">
          <Span className="swan-visually-hidden">{t("price-platform.token-price.sale-price")}</Span>
          {formattedDiscount}
        </Span>
      </Span>
    );
  }

  return <Span className="swan-list-price">{formattedList}</Span>;
};

export default ComparativePrice;
export { formatPrice };
